/**
 * SpecialistPanel — the case's conversational privacy counsel.
 *
 * Ask anything about the particulars of this case. The specialist reads the
 * whole record across every module (overview and clocks, risk assessment,
 * documents, updates, action board, mitigations, notifications and roster,
 * jurisdictions, deadline alerts, people, approvals, audit trail) plus the
 * verbatim HIPAA corpus and the Law Library, and answers with grounded,
 * cited advice. Read-only: it advises, the officer acts. Strictly
 * user-triggered; every exchange is kept as reviewable history.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const STARTERS = [
  'Where does this case stand and what should I prioritize next?',
  'Which notification clocks are running, for which states, and when do they expire?',
  'What must the individual notification letter contain under 164.404(c)?',
  'Is our documentation strong enough to defend the current determination under 164.414?',
];

export default function SpecialistPanel({ incidentId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [convId, setConvId] = useState(null);
  const [steps, setSteps] = useState([]);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/specialist`, { credentials: 'include' }).then((r) => r.json());
      if (r.success && r.messages?.length) {
        setMessages(r.messages.map((m) => ({ role: m.role, content: m.content })));
        setConvId(r.messages[r.messages.length - 1].conversation_id);
      }
    } catch { /* fresh conversation */ }
  }, [incidentId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, busy]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(''); setSteps([]);
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/specialist`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, conversation_id: convId }),
      }).then((r) => r.json());
      if (!r.success) throw new Error(r.message || 'No answer');
      setConvId(r.conversation_id);
      setMessages((m) => [...m, { role: 'assistant', content: r.reply }]);
      setSteps(r.suggested_next_steps || []);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `Sorry, ${e.message}. Try again.` }]);
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl flex flex-col h-[640px] shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-xs font-bold">✦</div>
        <div>
          <div className="text-sm font-semibold text-slate-800">Privacy Specialist</div>
          <div className="text-[10px] text-slate-400">Reads the whole case record and the regulations. Advises only; you decide.</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[220px]">
        {!messages.length && (
          <div className="space-y-2">
            <div className="text-[12px] text-slate-400">Things privacy officers ask:</div>
            {STARTERS.map((s, i) => (
              <button key={i} onClick={() => ask(s)} className="block w-full text-left text-[12px] text-slate-600 bg-slate-50 hover:bg-orange-50 hover:text-orange-700 border border-slate-200 rounded-lg px-3 py-2">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : ''}>
            <div className={`max-w-[92%] rounded-xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-orange-600 text-white' : 'bg-slate-50 text-slate-700 border border-slate-100'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-[12px] text-slate-400">
            <span className="inline-block w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            Reading the case record…
          </div>
        )}
        {!busy && steps.length > 0 && (
          <div className="border-t border-slate-100 pt-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Suggested next steps</div>
            {steps.map((s, i) => <div key={i} className="text-[12px] text-slate-600">→ {s}</div>)}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <input
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && ask()}
          placeholder="Ask about this case…"
          className="flex-1 text-[13px] border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400"
          disabled={busy}
        />
        <button onClick={() => ask()} disabled={busy || !input.trim()} className="px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow disabled:opacity-40">Send</button>
      </div>
    </div>
  );
}
